// Libraries
import { Fragment, useState } from 'react';
import { QuestionOutlineIcon } from '@chakra-ui/icons';
import { Flex, List, ListIcon, ListItem, Text, Tooltip } from '@chakra-ui/react';
// Types
import { TextWithTooltipProps } from './TextWithTooltip.types';
import { CircleIcon } from '@atoms/index';

/**
 * A text component with an optional tooltip.
 *
 * @component
 * @param {TextWithTooltipProps} props - The props for the TextWithTooltip component.
 * @param {string} props.tooltipText - The text to be displayed in the tooltip.
 * @param {ReactNode} props.children - The children elements to be rendered.
 * @param {object} props.rest - Any remaining properties of chakra Text.
 * @returns {ReactElement} The TextWithTooltip component.
 */
export const TextWithTooltip = ({ tooltipTitle, tooltipText, itemsList, children, ...rest }: TextWithTooltipProps) => {
  const [isLabelOpen, setIsLabelOpen] = useState(false);
  return (
    <Flex align='center' gap={1}>
      <Text {...rest}>{children}</Text>
      {!!tooltipText && (
        <Tooltip
          hasArrow
          placement='top'
          openDelay={100}
          closeDelay={200}
          gutter={12}
          bg='white'
          isOpen={isLabelOpen}
          label={
            <Fragment>
              {!!tooltipTitle && (
                <Text fontSize='s' lineHeight={2} fontWeight={600}>
                  {tooltipTitle}
                </Text>
              )}
              {!!tooltipText && (
                <Text fontSize='s' variant='faded'>
                  {tooltipText}
                </Text>
              )}
              {!!itemsList && (
                <List>
                  {itemsList.map((item, i) => (
                    <ListItem key={i}>
                      <Flex alignItems='center'>
                        <ListIcon as={CircleIcon} color='text.faded' boxSize={0.5} />
                        <Text fontSize='s' variant='faded'>
                          {item}
                        </Text>
                      </Flex>
                    </ListItem>
                  ))}
                </List>
              )}
            </Fragment>
          }
        >
          <QuestionOutlineIcon
            transition='all 0.2s ease-in-out'
            color='grey.100'
            boxSize={2.5}
            onMouseEnter={() => setIsLabelOpen(true)}
            onMouseLeave={() => setIsLabelOpen(false)}
            onClick={() => setIsLabelOpen((s) => !s)}
            _hover={{
              color: 'base',
              cursor: 'context-menu',
            }}
          />
        </Tooltip>
      )}
    </Flex>
  );
};
